import type { ConditionDescriptor } from "../../../../../api/types/fieldConfigurationTypes";
import type { DictionarySearchRequest } from "../../../../../api/types/svTypes";
import { MAX_DICTIONARY_SEARCH_LENGTH } from "@entrilia/shared/components/reporting/text-filter-grid/textFilterGridTypes";
import type { DashboardFilter } from "../../dashboard.types";

export type FilterValuesRequest = Omit<DictionarySearchRequest, "range" | "sort">;

export interface FilterValuesQuery {
  request: FilterValuesRequest;
  queryKey: string;
}

export const toFilterValue = (value: unknown): string | null =>
  value === null || value === undefined ? null : String(value);

const createQuery = (filter: DashboardFilter, request: FilterValuesRequest): FilterValuesQuery => ({
  request,
  queryKey: JSON.stringify([filter.dataSetId, request]),
});

export const buildFilterValuesQuery = ({
  filter,
  searchTerm,
  parentCondition,
  onlySelectedValues,
}: {
  filter: DashboardFilter;
  searchTerm: string;
  parentCondition?: ConditionDescriptor;
  onlySelectedValues?: unknown[];
}): FilterValuesQuery => {
  const trimmedSearchTerm = searchTerm.trim().slice(0, MAX_DICTIONARY_SEARCH_LENGTH);
  return createQuery(filter, {
    dimensionName: filter.id,
    searchTermUserInput: trimmedSearchTerm === "" ? undefined : trimmedSearchTerm,
    conditions: parentCondition === undefined ? undefined : [parentCondition],
    selectedValuesUserInput: onlySelectedValues?.map(toFilterValue),
  });
};

export const getSurvivingSelection = (
  selectedValues: unknown[],
  survivingValues: ReadonlySet<string | null>
): (string | null)[] =>
  selectedValues.map(toFilterValue).filter((value) => value === null || survivingValues.has(value));

export const buildFilterValuesRevalidationQuery = ({
  filter,
  selectedValues,
  parentCondition,
}: {
  filter: DashboardFilter;
  selectedValues: unknown[];
  parentCondition?: ConditionDescriptor;
}): FilterValuesQuery | undefined => {
  const selectedValuesUserInput = selectedValues
    .map(toFilterValue)
    .filter((value): value is string => value !== null)
    .filter((value, index, values) => values.indexOf(value) === index);
  if (selectedValuesUserInput.length === 0) {
    return undefined;
  }

  return createQuery(filter, {
    dimensionName: filter.id,
    conditions: parentCondition === undefined ? undefined : [parentCondition],
    selectedValuesUserInput,
    allRows: true,
  });
};
